import { useModel } from '@umijs/max';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { io, Socket } from 'socket.io-client';

type EventHandler<T = any> = (payload: T) => void;

interface SocketContextType {
  socket: Socket | null;
  on: <T = any>(event: string, handler: EventHandler<T>) => void;
  off: (event: string, handler?: EventHandler) => void;
  emit: <T = any>(event: string, payload?: T) => void;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  on: () => {},
  off: () => {},
  emit: () => {},
  isConnected: false,
});

interface SocketProviderProps {
  url: string;
  autoConnect?: boolean;
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({
  url,
  autoConnect = true,
  children,
}) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const { session } = useModel('user');
  const tokenRef = useRef<string | undefined>(session?.access_token);

  // Initialize socket only once
  useEffect(() => {
    if (socketRef.current) {
      return; // Socket already exists
    }

    if (!url) {
      console.error('SocketProvider: URL is required to initialize socket');
      return;
    }

    if (!session?.access_token) {
      console.error('SocketProvider: Token is required to initialize socket');
      return;
    }

    const socketInstance = io(url + '?token=' + session?.access_token, {
      autoConnect,
      transports: ['websocket'],
      auth: {
        token: session?.access_token,
      },
    });

    socketInstance.on('connect', () => {
      console.log('SocketProvider: Socket connected');
      setIsConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('SocketProvider: Socket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('SocketProvider: Connection error:', error);
    });

    socketRef.current = socketInstance;

    // auto connect socket
    socketRef.current.connect();

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [url, autoConnect, session?.access_token]);

  // Update token when it changes without recreating socket
  useEffect(() => {
    if (socketRef.current && session?.access_token !== tokenRef.current) {
      console.log('SocketProvider: Updating token');
      (socketRef.current.auth as any).token = session?.access_token;
      tokenRef.current = session?.access_token;

      // Reconnect with new token if socket is connected
      if (socketRef.current.connected) {
        socketRef.current.disconnect();
        socketRef.current.connect();
      }
    }
  }, [session?.access_token]);

  const on = useCallback(
    <T = any,>(event: string, handler: EventHandler<T>) => {
      socketRef.current?.on(event, handler);
    },
    [],
  );

  const off = useCallback((event: string, handler?: EventHandler) => {
    socketRef.current?.off(event, handler);
  }, []);

  const emit = useCallback(<T = any,>(event: string, payload?: T) => {
    socketRef.current?.emit(event, payload);
  }, []);

  const value: SocketContextType = {
    socket: socketRef.current,
    on,
    off,
    emit,
    isConnected,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export default SocketContext;
